import { fail } from './errors.js';

/*
 * 센터 프로그램(관리자 > 프로그램 관리): 센터가 운영하는 치료·상담·평가 프로그램 목록.
 * 프로그램마다 치료 영역·이름·방식·장소·담당 선생님·가격(여러 개)·기본가격·사용여부를 둔다.
 * 수업 만들기에서 프로그램을 먼저 고르면 그 프로그램에 등록된 선생님만 담당으로 고를 수 있다.
 * 아동 정보는 담지 않는다.
 */

export const PROGRAM_CATEGORIES = ['재활', '상담', '평가', '교육', '기타'] as const;
export type ProgramCategory = (typeof PROGRAM_CATEGORIES)[number];
export const PROGRAM_MODES = { INDIVIDUAL: '개인', GROUP: '그룹' } as const;
export type ProgramMode = keyof typeof PROGRAM_MODES;
export const PROGRAM_PLACES = { CENTER: '기관', VISIT: '방문', ONLINE: '비대면' } as const;
export type ProgramPlace = keyof typeof PROGRAM_PLACES;
/** 치료 영역 예시(직접 적어도 된다) */
export const PROGRAM_AREA_SUGGESTIONS = ['언어', '놀이', '감각통합', '미술', '음악', '인지학습', '사회성', '심리상담', '심리평가', '작업'] as const;

export const PROGRAM_LIMITS = { area: 20, name: 40, prices: 5, priceMax: 1_000_000, teachers: 30 } as const;

export interface CenterProgram {
  id: string;
  centerId: string;
  category: ProgramCategory;
  area: string;          // 치료 영역: 감각통합, 놀이 …
  name: string;          // 발달재활, 일반 - 감통치료, 지역사회 - 아청심리 …
  mode: ProgramMode;
  place: ProgramPlace;
  teacherIds: string[];  // 담당 선생님(직원 명부의 userId)
  prices: number[];      // 받는 가격들(원)
  basePrice: number;     // 기본가격(새 일정·수업의 기본값)
  active: boolean;       // 사용여부: 정상(true) / 중단(false)
  version: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export type ProgramInput = Pick<CenterProgram, 'category' | 'area' | 'name' | 'mode' | 'place' | 'teacherIds' | 'prices' | 'basePrice' | 'active'>;

/** 목록·선택지에 쓰는 이름: [감각통합] - 발달재활 */
export const programLabel = (p: Pick<CenterProgram, 'area' | 'name'>) => (p.area ? `[${p.area}] - ${p.name}` : p.name);

const text = (v: unknown, max: number, label: string, required: boolean) => {
  const s = typeof v === 'string' ? v.trim() : '';
  if (required && !s) fail('VALIDATION', `${label}을(를) 입력하세요.`);
  if (s.length > max) fail('VALIDATION', `${label}은(는) ${max}자 이하로 입력하세요.`);
  return s;
};
const won = (v: unknown, label: string) => {
  const n = Number(v);
  if (!Number.isInteger(n) || n < 0 || n > PROGRAM_LIMITS.priceMax) fail('VALIDATION', `${label}은(는) 0~${PROGRAM_LIMITS.priceMax.toLocaleString('ko-KR')}원 사이의 정수로 입력하세요.`);
  return n;
};

/**
 * 입력 검증. 담당 선생님은 이 센터 직원 명부에 있는 사람만(중복 제거). 가격은 1~5개, 기본가격은 가격 중 하나.
 */
export function validateProgram(i: Partial<ProgramInput>, staffIds: ReadonlySet<string>): ProgramInput {
  const category = PROGRAM_CATEGORIES.includes(i.category as ProgramCategory) ? (i.category as ProgramCategory) : fail('VALIDATION', '구분을 고르세요.');
  const mode = (Object.keys(PROGRAM_MODES) as ProgramMode[]).includes(i.mode as ProgramMode) ? (i.mode as ProgramMode) : fail('VALIDATION', '방식을 고르세요.');
  const place = (Object.keys(PROGRAM_PLACES) as ProgramPlace[]).includes(i.place as ProgramPlace) ? (i.place as ProgramPlace) : fail('VALIDATION', '장소를 고르세요.');
  const teacherIds = [...new Set(Array.isArray(i.teacherIds) ? i.teacherIds : [])];
  if (teacherIds.length > PROGRAM_LIMITS.teachers) fail('VALIDATION', `담당 선생님은 ${PROGRAM_LIMITS.teachers}명까지입니다.`);
  if (teacherIds.some((id) => !staffIds.has(id))) fail('VALIDATION', '직원 명부에 없는 선생님이 있습니다.');
  const prices = [...new Set((Array.isArray(i.prices) ? i.prices : []).map((p) => won(p, '가격')))];
  if (!prices.length) fail('VALIDATION', '가격을 하나 이상 입력하세요(무료는 0).');
  if (prices.length > PROGRAM_LIMITS.prices) fail('VALIDATION', `가격은 ${PROGRAM_LIMITS.prices}개까지입니다.`);
  const basePrice = i.basePrice === undefined ? prices[0]! : won(i.basePrice, '기본가격');
  if (!prices.includes(basePrice)) fail('VALIDATION', '기본가격은 입력한 가격 중 하나여야 합니다.');
  return {
    category, mode, place, teacherIds, prices, basePrice,
    area: text(i.area, PROGRAM_LIMITS.area, '치료 영역', false),
    name: text(i.name, PROGRAM_LIMITS.name, '프로그램 이름', true),
    active: i.active === undefined ? true : Boolean(i.active),
  };
}

export function newProgram(i: { id: string; centerId: string; input: Partial<ProgramInput>; staffIds: ReadonlySet<string>; by: string; now: string }): CenterProgram {
  return { id: i.id, centerId: i.centerId, ...validateProgram(i.input, i.staffIds), version: 1, createdAt: i.now, updatedAt: i.now, updatedBy: i.by };
}

export function updateProgram(cur: CenterProgram, input: Partial<ProgramInput>, staffIds: ReadonlySet<string>, by: string, now: string): CenterProgram {
  return { ...cur, ...validateProgram(input, staffIds), version: cur.version + 1, updatedAt: now, updatedBy: by };
}

/** 같은 구분·영역·이름의 프로그램이 이미 있는지(대소문자·공백 무시) */
export const sameProgram = (a: Pick<CenterProgram, 'category' | 'area' | 'name'>, b: Pick<CenterProgram, 'category' | 'area' | 'name'>) =>
  [a.category, a.area, a.name].join('|').replace(/\s+/g, '').toLowerCase() === [b.category, b.area, b.name].join('|').replace(/\s+/g, '').toLowerCase();

/** 목록 순서: 정상 먼저, 그다음 구분·영역·이름 가나다순 */
export function sortPrograms<T extends Pick<CenterProgram, 'active' | 'category' | 'area' | 'name'>>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => Number(b.active) - Number(a.active) || a.category.localeCompare(b.category, 'ko') || a.area.localeCompare(b.area, 'ko') || a.name.localeCompare(b.name, 'ko'));
}

/** 수업 분야 추정: 프로그램의 치료 영역 → 수업 분야(언어·사회성·감각·운동·학습). 모르면 undefined */
export function courseFieldOf(area: string): 'LANGUAGE' | 'SOCIAL' | 'SENSORY' | 'MOTOR' | 'LEARNING' | undefined {
  if (/언어|말/.test(area)) return 'LANGUAGE';
  if (/감각|감통/.test(area)) return 'SENSORY';
  if (/놀이|사회|미술|음악|심리|상담/.test(area)) return 'SOCIAL';
  if (/운동|작업|체육/.test(area)) return 'MOTOR';
  if (/인지|학습/.test(area)) return 'LEARNING';
  return undefined;
}

/** 프로그램 이름 비교용(공백·대소문자 무시) */
const nameKey = (s: string) => s.replace(/\s+/g, '').toLowerCase();

/**
 * 예전 일정에 적힌 프로그램 이름으로 프로그램 관리 목록의 초안을 만든다(이미 있는 이름은 건너뛴다).
 * 담당 선생님은 그 프로그램 일정을 맡았던 선생님들이다. 가격은 일정에 없으므로 0원으로 두고 원장님이 채운다.
 * 이미 만든 일정은 그대로 둔다(그 때의 프로그램 이름이 기록으로 남는다).
 */
export function programsFromSessions(sessions: ReadonlyArray<{ program: string; therapistId: string }>, existing: ReadonlyArray<Pick<CenterProgram, 'name' | 'area'>>, staffIds: ReadonlySet<string>): ProgramInput[] {
  const have = new Set(existing.flatMap((p) => [nameKey(p.name), nameKey(programLabel(p))]));
  const by = new Map<string, { name: string; teachers: Set<string> }>();
  for (const s of sessions) {
    const name = s.program.trim().slice(0, PROGRAM_LIMITS.name);
    const k = nameKey(name);
    if (!name || have.has(k)) continue;
    const row = by.get(k) ?? { name, teachers: new Set<string>() };
    if (staffIds.has(s.therapistId) && row.teachers.size < PROGRAM_LIMITS.teachers) row.teachers.add(s.therapistId);
    by.set(k, row);
  }
  return [...by.values()].map(({ name, teachers }) => ({
    category: /평가|검사/.test(name) ? '평가' : /상담|심리/.test(name) ? '상담' : '재활',
    area: name.replace(/\s*(치료|그룹|평가|상담)$/, '').slice(0, PROGRAM_LIMITS.area),
    name, mode: /그룹/.test(name) ? 'GROUP' : 'INDIVIDUAL', place: 'CENTER',
    teacherIds: [...teachers], prices: [0], basePrice: 0, active: true,
  }));
}
